import { useDispatch, useSelector, useStore } from 'react-redux'
import {
  addBatchToCart,
  emptyCart,
  getPayloadProduct,
  isLoadingProduct,
  setCartRepricePending,
} from 'redux/actions/cartActions'
import { addPriceList } from 'redux/actions/cotizacionActions'
import { getProductPriceInventoryService } from 'redux/services/clientsServices'
import { isCartRoute } from 'Utils/isCartRoute'
import { isWebDocumentOrigin } from 'Utils/isWebDocumentOrigin'
import { fethCheckFinalPrice } from './checkFinalPriceUtils'
import { getCartPriceChanges } from './getCartPriceChanges'

export const useCheckFinalPrice = () => {
  const dispatch = useDispatch()
  const store = useStore()
  const company = useSelector(state => state.company)
  const quoteHeader = useSelector(state => state.cotizacion?.QuoteHeader)
  const cart = useSelector(state => state.cart.cart)

  // Callers that dispatch setCompany before calling must pass the company
  // they are leaving; otherwise the store value may already be the new one.
  const isCompanyChangeFrom = (newCompany, previousCompanyId) =>
    !!newCompany?.CompanyId &&
    newCompany.CompanyId !== (previousCompanyId ?? company.CompanyId)

  // A price list without a customer must not leak into another company,
  // including subsequent additions to an initially empty cart. Web documents
  // keep their own prices, so their price list is left untouched.
  const clearPreviousPriceList = (customerId, isCompanyChange) => {
    if (
      !customerId &&
      isCompanyChange &&
      !isWebDocumentOrigin(quoteHeader?.Origin)
    ) {
      dispatch(addPriceList(undefined))
    }
  }

  const checkFinalPrice = async ({
    prods,
    customerId,
    newCompany,
    previousCompanyId,
    isClone,
    confirmRemoval,
  } = {}) => {
    const isCompanyChange = isCompanyChangeFrom(newCompany, previousCompanyId)
    const isWebDocument = isWebDocumentOrigin(quoteHeader?.Origin)
    dispatch(isLoadingProduct({ isLoadingAllProducts: true }))
    const productList = prods || cart
    if (!productList.length) {
      clearPreviousPriceList(customerId, isCompanyChange)
      dispatch(setCartRepricePending(null))
      if (prods) dispatch(emptyCart())
      return dispatch(isLoadingProduct({ isLoadingAllProducts: false }))
    }
    // Repricing the current cart is discarded if the cart was emptied or
    // replaced meanwhile (e.g. a screen loading quote lines), so stale lines
    // are never written back over it
    const isCartReplaced = () =>
      !prods && store.getState().cart.cart !== productList

    const companyCode = newCompany?.CompanyCode || company.CompanyCode
    const companyId = newCompany?.CompanyId || company.CompanyId
    const WarehouseId = newCompany?.WarehouseId || company.WarehouseId

    const batches = []
    for (let i = 0; i < productList.length; i += 99) {
      batches.push(productList.slice(i, i + 99))
    }

    try {
      // Fetch all batches in parallel instead of sequentially
      const batchResults = await Promise.all(
        batches.map(async batch => {
          const ProductIds = batch.map(product => product.ProductId)
          const combinedPayload = batch.map(prod => ({
            product: prod,
            qty: prod.Quantity,
          }))

          const priceInventoryPayload = {
            CompanyId: companyId,
            CompanyCode: companyCode,
            WarehouseId,
            ProductIds: ProductIds || [],
            UoMId: null,
            PriceListId: null,
          }

          // Both API calls for this batch run in parallel
          const [response, orderedProducts] = await Promise.all([
            getProductPriceInventoryService(priceInventoryPayload),
            fethCheckFinalPrice(
              combinedPayload,
              customerId,
              newCompany,
              isCompanyChange
            ),
          ])

          return {
            updatedProducts: response.data?.Data || [],
            orderedProducts: orderedProducts || [],
          }
        })
      )

      if (isCartReplaced()) return false

      const allUpdatedProducts = batchResults.flatMap(r => r.updatedProducts)
      const allOrderedProducts = batchResults.flatMap(r => r.orderedProducts)

      const refreshBasePrice = !!newCompany && !isWebDocument

      allOrderedProducts?.forEach((orderedProduct, index) => {
        const updatedProduct = allUpdatedProducts?.find(
          up =>
            up.ProductId?.toUpperCase() ===
            orderedProduct.selectList.ProductId?.toUpperCase()
        )
        if (refreshBasePrice && updatedProduct?.BasePrice == null) {
          console.warn(
            'Missing BasePrice after company/branch change; retaining the previous base price.',
            {
              ProductId: orderedProduct.selectList.ProductId,
              CompanyId: companyId,
              WarehouseId,
            }
          )
        }
        if (updatedProduct) {
          allOrderedProducts[index].payload.product = {
            ...orderedProduct.payload.product,
            Cost: updatedProduct.Cost,
            AvgCost: updatedProduct.AvgCost,
            AvgPrice: updatedProduct.AvgPrice,
            ...(refreshBasePrice
              ? {
                  BasePrice:
                    updatedProduct.BasePrice ??
                    orderedProduct.payload.product.BasePrice,
                }
              : {}),
          }
        }
      })

      // Build all cart items and dispatch in a single state update
      const cartItems = allOrderedProducts.map(prod => ({
        product: getPayloadProduct(
          prod.selectList,
          prod.payload,
          prod.warehouse,
          prod.Quantity,
          prod.Price,
          isClone
        ),
        qty: prod.qty,
      }))

      const { retainedItems, removedProducts } = getCartPriceChanges(
        productList,
        cartItems
      )

      if (removedProducts.length && confirmRemoval) {
        const proceed = await confirmRemoval(removedProducts)
        if (!proceed || isCartReplaced()) {
          return false
        }
      }

      clearPreviousPriceList(customerId, isCompanyChange)
      dispatch(emptyCart())
      dispatch(addBatchToCart(retainedItems))
      return true
    } catch (error) {
      // Lines passed in (editing or cloning a quote) must still reach the cart
      // when pricing fails; refreshing the current cart keeps it untouched.
      if (prods) {
        dispatch(emptyCart())
        dispatch(
          addBatchToCart(
            productList.map(product => ({ product, qty: product.Quantity }))
          )
        )
      }
      throw error
    } finally {
      // Always clear the loading flag regardless of success or failure
      dispatch(isLoadingProduct({ isLoadingAllProducts: false }))
    }
  }

  const deferCartReprice = ({ newCompany, previousCompanyId, customerId }) => {
    clearPreviousPriceList(
      customerId,
      isCompanyChangeFrom(newCompany, previousCompanyId)
    )
    dispatch(
      setCartRepricePending({
        previousCompanyId: previousCompanyId ?? company.CompanyId,
      })
    )
    return true
  }

  // Company or branch change. Only the cart screens reprice right away; on any
  // other screen a non-empty cart is marked and repriced once a cart screen
  // is opened (see HorizontalNav). Resolves to false if the change is cancelled.
  const applyCompanyChange = async ({ pathname, ...options }) =>
    isCartRoute(pathname) || !cart.length
      ? checkFinalPrice(options)
      : deferCartReprice(options)

  return { checkFinalPrice, applyCompanyChange }
}
